import { ApplicationInsights } from "@microsoft/applicationinsights-web";

import { CustomProperties } from "types/log/CustomProperties";
import { LogEntry } from "types/log/LogEntry";
import { LogSeverity } from "types/log/LogSeverityEnum";
import { IReleaseManifest } from "types/telemetry/IReleaseManifest";

import { REACT_APP_APPINSIGHTS_CONNECTION_STRING, REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY } from "../configs/config";

let appInsights: ApplicationInsights | null = null;
const isDevelopment = process.env.NODE_ENV === "development";

const INSTRUMENTATION_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Build-time release manifest.
 *
 * Values are sourced from `REACT_APP_*` environment variables which Create
 * React App inlines as string literals at bundle time via Webpack's
 * DefinePlugin. CI sets these in the deploy workflow before `npm run build`;
 * local dev leaves them unset and falls back to `"local"` so local bundles are
 * trivially distinguishable from production traffic in the App Insights portal.
 *
 * @see .github/workflows/*_muggle_prompt_ui_deployment.yml — CI env var wiring
 * @see src/types/telemetry/IReleaseManifest.ts — canonical field shape
 */
const RELEASE_MANIFEST: IReleaseManifest = {
  release: process.env.REACT_APP_RELEASE ?? "local",
  buildId: process.env.REACT_APP_BUILD_ID ?? "local",
  commitSha: process.env.REACT_APP_COMMIT_SHA ?? "local",
  buildTime: process.env.REACT_APP_BUILD_TIME ?? "local",
  serviceName: "muggle-ai-ui",
};

// Deploy environment — wired from REACT_APP_ENVIRONMENT in the staging /
// production deploy workflows. Lives outside the manifest because the manifest
// shape is shared with backend services where environment is a runtime concern,
// not a build-time one. Surfaces as `customDimensions.environment` so the
// versions dashboard can split UI rows into staging vs production columns.
const RELEASE_ENVIRONMENT: string = process.env.REACT_APP_ENVIRONMENT ?? "unknown";

/**
 * Telemetry initializer callback registered with `addTelemetryInitializer`.
 *
 * Attaches build-time release identity to every outbound App Insights envelope:
 * - Five canonical fields written to `item.data` land in `customDimensions` in the portal.
 * - `ai.application.ver` tag enables the native application-version blade filter.
 * - `ai.cloud.role` tag sets the service name shown in the Application Map.
 *
 * Exported for unit testing; callers should not register it directly — use
 * `initializeAppInsights` which registers it after `loadAppInsights()`.
 *
 * @param item - The outbound telemetry envelope (mutated in place).
 */
export const releaseIdentityInitializer = (item: { data?: Record<string, unknown>; tags?: Record<string, unknown> }) => {
  // Ensure the data bag exists before writing into it.
  item.data = item.data ?? {};
  item.data["release"] = RELEASE_MANIFEST.release;
  item.data["buildId"] = RELEASE_MANIFEST.buildId;
  item.data["commitSha"] = RELEASE_MANIFEST.commitSha;
  item.data["buildTime"] = RELEASE_MANIFEST.buildTime;
  item.data["serviceName"] = RELEASE_MANIFEST.serviceName;
  item.data["environment"] = RELEASE_ENVIRONMENT;

  // Ensure the tags bag exists before writing into it.
  item.tags = item.tags ?? {};
  // Native App Insights application-version tag — enables portal blade filtering.
  item.tags["ai.application.ver"] = RELEASE_MANIFEST.release;
  // Cloud role name — surfaces in Application Map and log queries.
  item.tags["ai.cloud.role"] = RELEASE_MANIFEST.serviceName;
};

export const initializeAppInsights = () => {
  // Only initialize if not already initialized
  if (appInsights) return appInsights;

  const connectionString = REACT_APP_APPINSIGHTS_CONNECTION_STRING?.trim();
  const instrumentationKey = REACT_APP_APPINSIGHTS_INSTRUMENTATIONKEY?.trim();
  // A bare key posts to the legacy global endpoint and 400s for region-bound resources; a
  // non-GUID key (unset placeholder) 400s on every event. Skip init unless we can target a
  // valid endpoint, so a misconfigured deploy stays silent instead of flooding /v2/track.
  const hasValidKey = Boolean(instrumentationKey && INSTRUMENTATION_KEY_PATTERN.test(instrumentationKey));
  if (isDevelopment || (!connectionString && !hasValidKey)) {
    console.debug("App Insights telemetry disabled: no connection string and no valid instrumentation key");
    return;
  }
  appInsights = new ApplicationInsights({
    config: {
      ...(connectionString ? { connectionString: connectionString } : { instrumentationKey: instrumentationKey }),
      enableAutoRouteTracking: true,
      enableCorsCorrelation: false,
      // Header tracking is disabled so the `Authorization: Bearer <JWT>` header
      // set on every authenticated backend call (see BaseApiService) is never
      // captured into a telemetry envelope and shipped to App Insights. The SDK
      // redacts a default header allowlist, but we do not rely on that default —
      // we opt out of header capture entirely.
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
      disableFetchTracking: false,
      enableDebug: isDevelopment,
      loggingLevelConsole: isDevelopment ? 1 : 0, // 1 = All logs, 0 = Only critical
    },
  });

  appInsights.loadAppInsights();

  /**
   * Register the release identity initializer.  Called synchronously for every
   * telemetry item before it leaves the browser — auto-collected page views,
   * dependency calls, exceptions, and explicit track* calls all pass through it.
   *
   * `addTelemetryInitializer` is the canonical browser SDK mechanism for
   * per-event enrichment.  The v3 browser SDK implementation is fully functional
   * (unlike the Node.js SDK's `addTelemetryProcessor` which was a no-op in some
   * versions).
   */
  appInsights.addTelemetryInitializer(releaseIdentityInitializer);

  return appInsights;
};

export const getAppInsights = () => appInsights;

// Core logging function
const log = ({ message, properties = {}, severity = LogSeverity.Information, error }: LogEntry) => {
  const timestamp = new Date().toISOString();
  const logData = { timestamp, message, ...properties };

  // Always send to Application Insights
  if (appInsights) {
    if (error) {
      appInsights.trackException({ error, severityLevel: severity, properties: logData });
    } else {
      appInsights.trackTrace({ message, properties: logData, severityLevel: severity });
    }
  }

  // Console logging in development
  if (isDevelopment) {
    const consoleMessage = `[${timestamp}] ${message}`;
    switch (severity) {
      case LogSeverity.Verbose:
        console.debug(consoleMessage, properties);
        break;
      case LogSeverity.Information:
        console.log(consoleMessage, properties);
        break;
      case LogSeverity.Warning:
        console.warn(consoleMessage, properties);
        break;
      case LogSeverity.Error:
      case LogSeverity.Critical:
        console.error(consoleMessage, error || properties);
        break;
      default:
        console.log(consoleMessage, properties);
    }
  }
};

// Public logging functions
export const logDebug = (message: string, properties?: CustomProperties) => {
  log({ message, properties, severity: LogSeverity.Verbose });
};

export const logInfo = (message: string, properties?: CustomProperties) => {
  log({ message, properties, severity: LogSeverity.Information });
};

export const logWarning = (message: string, properties?: CustomProperties) => {
  log({ message, properties, severity: LogSeverity.Warning });
};

export const logError = (message: string, error?: Error, properties?: CustomProperties) => {
  log({ message, error, properties, severity: LogSeverity.Error });
};

export const logCritical = (message: string, error?: Error, properties?: CustomProperties) => {
  log({ message, error, properties, severity: LogSeverity.Critical });
};

// Event tracking
export const logEvent = (name: string, properties?: CustomProperties) => {
  const isDevelopment = process.env.NODE_ENV === "development";

  appInsights?.trackEvent({ name }, properties);

  if (isDevelopment) {
    console.log(`[Event] ${name}`, properties);
  }
};

// Metric tracking
export const logMetric = (name: string, value: number, properties?: CustomProperties) => {
  const isDevelopment = process.env.NODE_ENV === "development";

  appInsights?.trackMetric({ name, average: value }, properties);

  if (isDevelopment) {
    console.log(`[Metric] ${name}: ${value}`, properties);
  }
};
